/**
 * UserFormModal Component
 * Modal for adding and editing users
 * Create mode: All fields editable
 * Edit mode: Username and email read-only, only role and plan editable
 */

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import { X } from 'lucide-react';
import { useCreateUser, useUpdateUser } from '../hooks/useUsers';
import type { User, UserFormMode, UserFormData } from '../types';
import { useNotification } from '../../../../hooks/useNotification';
import { usePackages } from '../../../../services/package.service';

// ============================================
// Component Props
// ============================================

interface UserFormModalProps {
  open: boolean;
  mode: UserFormMode;
  user?: User; // Only required for edit mode
  onClose: () => void;
}

// ============================================
// Initial Form State
// ============================================

const getInitialFormData = (mode: UserFormMode, user?: User): UserFormData => {
  if (mode === 'edit' && user) {
    return {
      userName: user.userName || '',
      email: user.email,
      role: user.role,
      plan: user.plan || '',
    };
  }

  return {
    userName: '',
    email: '',
    role: 'USER',
    plan: '',
  };
};

const isFreePackage = (pkg: {
  price: number;
  priceVnd?: number;
  prices?: Array<{ amountMinor: string | number; isActive?: boolean }>;
}) => {
  if (pkg.prices?.length) {
    return pkg.prices.some((price) => price.isActive !== false && Number(price.amountMinor) === 0);
  }

  return pkg.price === 0 && (pkg.priceVnd ?? 0) === 0;
};

// ============================================
// UserFormModal Component
// ============================================

export const UserFormModal: React.FC<UserFormModalProps> = ({ open, mode, user, onClose }) => {
  const { success } = useNotification();
  const createUserMutation = useCreateUser();
  const updateUserMutation = useUpdateUser();
  const { data: packages = [] } = usePackages();

  const [formData, setFormData] = useState<UserFormData>(getInitialFormData(mode, user));
  const [errors, setErrors] = useState<Partial<Record<keyof UserFormData, string>>>({});
  const packageOptions = packages
    .filter((pkg) => pkg.isActive || pkg.code === formData.plan)
    .map((pkg) => ({ code: pkg.code, name: pkg.name }));
  const selectedPackageMissing = formData.plan
    ? !packageOptions.some((pkg) => pkg.code === formData.plan)
    : false;

  // Reset form when modal opens or mode/user changes
  useEffect(() => {
    if (open) {
      setFormData(getInitialFormData(mode, user));
      setErrors({});
    }
  }, [open, mode, user]);

  useEffect(() => {
    if (!open || mode !== 'create' || formData.plan || packageOptions.length === 0) {
      return;
    }

    const freePackage =
      packages.find((pkg) => pkg.isActive && isFreePackage(pkg)) ??
      packages.find((pkg) => pkg.isActive);
    if (freePackage) {
      setFormData((prev) => ({ ...prev, plan: freePackage.code }));
    }
  }, [formData.plan, mode, open, packageOptions.length, packages]);

  // ============================================
  // Form Validation
  // ============================================

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof UserFormData, string>> = {};

    if (mode === 'create') {
      // Username validation
      if (!formData.userName.trim()) {
        newErrors.userName = 'Username is required';
      } else if (formData.userName.length < 3) {
        newErrors.userName = 'Username must be at least 3 characters';
      }

      // Email validation
      if (!formData.email.trim()) {
        newErrors.email = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = 'Invalid email format';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ============================================
  // Form Submission
  // ============================================

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      if (mode === 'create') {
        // Create new user
        await createUserMutation.mutateAsync({
          userName: formData.userName,
          email: formData.email,
          role: formData.role,
          plan: formData.plan || undefined,
        });
        success('User created successfully. Invitation email sent.');
      } else {
        // Update existing user (only role and plan)
        if (!user) return;
        await updateUserMutation.mutateAsync({
          publicId: user.publicId,
          data: {
            role: formData.role,
            plan: formData.plan || undefined,
          },
        });
        success('User updated successfully.');
      }

      onClose();
    } catch (error) {
      // Error handling is done by axios interceptor
      // Additional error handling can be added here if needed
      console.error('Form submission error:', error);
    }
  };

  // ============================================
  // Form Handlers
  // ============================================

  const handleChange = (field: keyof UserFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const isLoading = createUserMutation.isPending || updateUserMutation.isPending;
  const isReadOnly = mode === 'edit';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'rgb(30, 41, 59)' : 'white'),
          color: (theme) => (theme.palette.mode === 'dark' ? 'white' : 'rgb(17, 24, 39)'),
          borderRadius: 2,
        },
      }}
    >
      {/* ============================================ */}
      {/* Dialog Header */}
      {/* ============================================ */}
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span className="text-lg font-semibold">
          {mode === 'create' ? 'Add New User' : 'Edit User'}
        </span>
        <button
          onClick={onClose}
          className="rounded-lg p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-white"
          disabled={isLoading}
        >
          <X size={20} />
        </button>
      </DialogTitle>

      {/* ============================================ */}
      {/* Dialog Content */}
      {/* ============================================ */}
      <DialogContent dividers sx={{ borderColor: (theme) => (theme.palette.mode === 'dark' ? 'rgb(51, 65, 85)' : 'rgb(229, 231, 235)') }}>
        <div className="space-y-4">
          {/* Username Field */}
          <TextField
            fullWidth
            label="Username"
            value={formData.userName}
            onChange={(e) => handleChange('userName', e.target.value)}
            error={!!errors.userName}
            helperText={errors.userName || (isReadOnly ? 'Read-only' : '')}
            disabled={isLoading || isReadOnly}
            required={mode === 'create'}
            InputProps={{
              readOnly: isReadOnly,
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                color: (theme) => (theme.palette.mode === 'dark' ? 'white' : 'rgb(17, 24, 39)'),
                '& fieldset': { borderColor: (theme) => (theme.palette.mode === 'dark' ? 'rgb(71, 85, 105)' : 'rgb(209, 213, 219)') },
                '&:hover fieldset': { borderColor: (theme) => (theme.palette.mode === 'dark' ? 'rgb(100, 116, 139)' : 'rgb(156, 163, 175)') },
                '&.Mui-focused fieldset': { borderColor: '#3B82F6' },
              },
              '& .MuiInputLabel-root': { color: (theme) => (theme.palette.mode === 'dark' ? 'rgb(148, 163, 184)' : 'rgb(107, 114, 128)') },
              '& .MuiFormHelperText-root': { color: errors.userName ? '#ef4444' : (theme) => (theme.palette.mode === 'dark' ? 'rgb(148, 163, 184)' : 'rgb(107, 114, 128)') },
            }}
          />

          {/* Email Field */}
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={formData.email}
            onChange={(e) => handleChange('email', e.target.value)}
            error={!!errors.email}
            helperText={errors.email || (isReadOnly ? 'Read-only' : '')}
            disabled={isLoading || isReadOnly}
            required={mode === 'create'}
            InputProps={{
              readOnly: isReadOnly,
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                color: (theme) => (theme.palette.mode === 'dark' ? 'white' : 'rgb(17, 24, 39)'),
                '& fieldset': { borderColor: (theme) => (theme.palette.mode === 'dark' ? 'rgb(71, 85, 105)' : 'rgb(209, 213, 219)') },
                '&:hover fieldset': { borderColor: (theme) => (theme.palette.mode === 'dark' ? 'rgb(100, 116, 139)' : 'rgb(156, 163, 175)') },
                '&.Mui-focused fieldset': { borderColor: '#3B82F6' },
              },
              '& .MuiInputLabel-root': { color: (theme) => (theme.palette.mode === 'dark' ? 'rgb(148, 163, 184)' : 'rgb(107, 114, 128)') },
              '& .MuiFormHelperText-root': { color: errors.email ? '#ef4444' : (theme) => (theme.palette.mode === 'dark' ? 'rgb(148, 163, 184)' : 'rgb(107, 114, 128)') },
            }}
          />

          {/* Role Field */}
          <FormControl fullWidth>
            <InputLabel
              sx={{
                color: (theme) => (theme.palette.mode === 'dark' ? 'rgb(148, 163, 184)' : 'rgb(107, 114, 128)'),
                '&.Mui-focused': { color: '#3B82F6' },
              }}
            >
              Role
            </InputLabel>
            <Select
              value={formData.role}
              onChange={(e) => handleChange('role', e.target.value)}
              disabled={isLoading}
              label="Role"
              sx={{
                color: (theme) => (theme.palette.mode === 'dark' ? 'white' : 'rgb(17, 24, 39)'),
                '& .MuiOutlinedInput-notchedOutline': { borderColor: (theme) => (theme.palette.mode === 'dark' ? 'rgb(71, 85, 105)' : 'rgb(209, 213, 219)') },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: (theme) => (theme.palette.mode === 'dark' ? 'rgb(100, 116, 139)' : 'rgb(156, 163, 175)') },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#3B82F6' },
                '& .MuiSvgIcon-root': { color: (theme) => (theme.palette.mode === 'dark' ? 'white' : 'rgb(17, 24, 39)') },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'rgb(30, 41, 59)' : 'white'),
                    color: (theme) => (theme.palette.mode === 'dark' ? 'white' : 'rgb(17, 24, 39)'),
                  },
                },
              }}
            >
              <MenuItem value="USER">User</MenuItem>
              <MenuItem value="ADMIN">Admin</MenuItem>
            </Select>
          </FormControl>

          {/* Plan Field */}
          <FormControl fullWidth>
            <InputLabel
              sx={{
                color: (theme) => (theme.palette.mode === 'dark' ? 'rgb(148, 163, 184)' : 'rgb(107, 114, 128)'),
                '&.Mui-focused': { color: '#3B82F6' },
              }}
            >
              Plan
            </InputLabel>
            <Select
              value={formData.plan}
              onChange={(e) => handleChange('plan', e.target.value)}
              disabled={isLoading}
              label="Plan"
              sx={{
                color: (theme) => (theme.palette.mode === 'dark' ? 'white' : 'rgb(17, 24, 39)'),
                '& .MuiOutlinedInput-notchedOutline': { borderColor: (theme) => (theme.palette.mode === 'dark' ? 'rgb(71, 85, 105)' : 'rgb(209, 213, 219)') },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: (theme) => (theme.palette.mode === 'dark' ? 'rgb(100, 116, 139)' : 'rgb(156, 163, 175)') },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#3B82F6' },
                '& .MuiSvgIcon-root': { color: (theme) => (theme.palette.mode === 'dark' ? 'white' : 'rgb(17, 24, 39)') },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'rgb(30, 41, 59)' : 'white'),
                    color: (theme) => (theme.palette.mode === 'dark' ? 'white' : 'rgb(17, 24, 39)'),
                  },
                },
              }}
            >
              <MenuItem value="">
                <em>Auto free package</em>
              </MenuItem>
              {selectedPackageMissing && <MenuItem value={formData.plan}>{formData.plan}</MenuItem>}
              {packageOptions.map((pkg) => (
                <MenuItem key={pkg.code} value={pkg.code}>
                  {pkg.name} ({pkg.code})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
      </DialogContent>

      {/* ============================================ */}
      {/* Dialog Actions */}
      {/* ============================================ */}
      <DialogActions sx={{ p: 2, borderColor: (theme) => (theme.palette.mode === 'dark' ? 'rgb(51, 65, 85)' : 'rgb(229, 231, 235)') }}>
        <Button
          onClick={onClose}
          disabled={isLoading}
          sx={{
            color: (theme) => (theme.palette.mode === 'dark' ? 'rgb(148, 163, 184)' : 'rgb(107, 114, 128)'),
            '&:hover': { bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'rgb(51, 65, 85)' : 'rgb(243, 244, 246)') },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={isLoading}
          variant="contained"
          sx={{
            bgcolor: '#3B82F6',
            '&:hover': { bgcolor: '#2563eb' },
            '&:disabled': { bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'rgb(71, 85, 105)' : 'rgb(209, 213, 219)'), color: (theme) => (theme.palette.mode === 'dark' ? 'rgb(148, 163, 184)' : 'rgb(156, 163, 175)') },
          }}
          startIcon={isLoading ? <CircularProgress size={16} /> : null}
        >
          {isLoading ? 'Saving...' : mode === 'create' ? 'Create User' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
